import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import LOIPreview from '../components/LOIPreview';
import LOISignaturePad from '../components/LOISignaturePad';
import { BUILD_ID } from '../components/Sidebar';
import Sidebar from '../components/Sidebar';
import TradosPublicHeader from '../components/TradosPublicHeader';
import WPChrome from '../components/WPChrome';
import useCanonical from '../hooks/useCanonical';
import usePageTitle from '../hooks/usePageTitle';

// --- Cookie helpers ---
function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : '';
}

// --- Constants ---

const COUNTRIES = [
  'Argentina', 'Australia', 'Brazil', 'Canada', 'Chile', 'China', 'Colombia',
  'Egypt', 'France', 'Germany', 'India', 'Indonesia', 'Italy', 'Japan',
  'Mexico', 'Netherlands', 'Nigeria', 'Portugal', 'Saudi Arabia', 'Singapore',
  'South Africa', 'South Korea', 'Spain', 'Switzerland', 'Thailand', 'Turkey',
  'UAE', 'United Kingdom', 'United States', 'Vietnam'
];

const COMMODITY_TYPES = [
  'Sugar ICUMSA 45', 'Sugar ICUMSA 150', 'Soybeans', 'Coffee Arabica',
  'Coffee Robusta', 'Corn', 'Chicken', 'Beef', 'Iron Ore', 'Ethanol', 'Other'
];

const INCOTERMS = [
  'FOB', 'CIF', 'CFR', 'EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS'
];

const PAYMENT_TERMS = [
  'Documentary Letter of Credit (DLC)', 'Standby Letter of Credit (SBLC)',
  'Telegraphic Transfer (TT)', 'MT799', 'MT760',
  'Cash Against Documents (CAD)', 'Advance Payment', 'Open Account'
];

const REQUEST_TIMEFRAMES = [
  'Spot (One-time)', '1 Year Contract', '2 Year Contract', 'Other'
];

const QUANTITY_UNITS = [
  'Metric Tons', 'Short Tons', 'Long Tons', 'Kilograms', 'Pounds',
  'Bushels', 'Liters', 'Gallons', 'Barrels', 'Bags (60kg)'
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'BRL', 'CNY', 'JPY', 'AED', 'CHF', 'CAD', 'AUD'];

const CONTRACT_DURATIONS = [
  '30 Days', '60 Days', '90 Days', '6 Months', '1 Year', '2 Years', 'Spot / One-time'
];

const INSPECTION_TERMS = [
  'SGS Inspection at Origin', 'Bureau Veritas at Origin',
  'Intertek at Origin', 'SGS at Destination', 'Bureau Veritas at Destination',
  'Buyer Appointed Inspector', 'Seller Appointed Inspector',
  'Joint Inspection', 'No Inspection Required'
];

const PORTS = [
  'Santos - Brazil', 'Paranagua - Brazil', 'Rio Grande - Brazil', 'Itajai - Brazil',
  'Sao Francisco do Sul - Brazil', 'Vitoria - Brazil', 'Salvador - Brazil',
  'Recife - Brazil', 'Manaus - Brazil', 'Belem - Brazil',
  'Shanghai - China', 'Ningbo-Zhoushan - China', 'Shenzhen - China', 'Guangzhou - China',
  'Qingdao - China', 'Tianjin - China', 'Dalian - China', 'Xiamen - China',
  'Singapore - Singapore',
  'Rotterdam - Netherlands', 'Antwerp - Belgium', 'Hamburg - Germany',
  'Busan - South Korea', 'Yokohama - Japan', 'Tokyo - Japan', 'Kobe - Japan',
  'Hong Kong - China', 'Kaohsiung - Taiwan',
  'Dubai (Jebel Ali) - UAE', 'Abu Dhabi - UAE',
  'Mumbai (Nhava Sheva) - India', 'Chennai - India', 'Kolkata - India',
  'Ho Chi Minh City - Vietnam', 'Haiphong - Vietnam',
  'Bangkok (Laem Chabang) - Thailand', 'Jakarta (Tanjung Priok) - Indonesia',
  'Port Klang - Malaysia', 'Manila - Philippines',
  'Los Angeles - United States', 'Long Beach - United States',
  'New York/New Jersey - United States', 'Houston - United States',
  'Savannah - United States', 'Seattle - United States', 'Miami - United States',
  'Vancouver - Canada', 'Montreal - Canada',
  'Felixstowe - United Kingdom', 'London Gateway - United Kingdom',
  'Le Havre - France', 'Marseille - France',
  'Barcelona - Spain', 'Valencia - Spain', 'Algeciras - Spain',
  'Genoa - Italy', 'La Spezia - Italy',
  'Piraeus - Greece', 'Istanbul (Ambarli) - Turkey', 'Mersin - Turkey',
  'Alexandria - Egypt', 'Port Said - Egypt',
  'Durban - South Africa', 'Cape Town - South Africa',
  'Lagos (Apapa) - Nigeria', 'Mombasa - Kenya', 'Dar es Salaam - Tanzania',
  'Buenos Aires - Argentina', 'Valparaiso - Chile', 'Callao - Peru',
  'Cartagena - Colombia', 'Veracruz - Mexico', 'Manzanillo - Mexico',
  'Jeddah - Saudi Arabia', 'Dammam - Saudi Arabia',
  'Bandar Abbas - Iran', 'Karachi - Pakistan',
  'Colombo - Sri Lanka', 'Chittagong - Bangladesh',
  'Other'
];

const STEP_LABELS = [
  'Your Company',
  'Seller & Product',
  'Deal Terms',
  'Timeline',
  'Review & Sign'
];

// --- Sub-components ---

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-0 py-6">
      {STEP_LABELS.map((label, idx) => {
        const step = idx + 1;
        const isCompleted = step < currentStep;
        const isActive = step === currentStep;
        const isUpcoming = step > currentStep;

        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
                  isCompleted
                    ? 'bg-[#2B4B6A] text-white'
                    : isActive
                    ? 'border-2 border-[#2B4B6A] text-[#2B4B6A] shadow-md shadow-blue-200'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <span
                className={`text-xs font-medium whitespace-nowrap ${
                  isActive ? 'text-[#2B4B6A]' : isCompleted ? 'text-gray-600' : 'text-gray-400'
                }`}
              >
                {label}
              </span>
            </div>
            {step < 5 && (
              <div
                className={`h-0.5 w-12 mt-[-18px] ${
                  step < currentStep ? 'bg-[#2B4B6A]' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-lg font-semibold text-gray-800 mb-4">{children}</h2>;
}

function FieldRow({ children, cols = 2 }) {
  return (
    <div className={`grid gap-4 ${cols === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
      {children}
    </div>
  );
}

function FieldGroup({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

function SearchablePortSelect({ value, onChange, placeholder }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = PORTS.filter(p => p.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <Input
        value={open ? search : value || ''}
        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.map(p => (
            <button
              key={p}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${value === p ? 'bg-blue-50 font-medium' : ''}`}
              onClick={() => { onChange(p); setOpen(false); setSearch(''); }}
            >
              {p}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No ports found</div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Main Component ---

const LOI_STORAGE_KEY = 'trados_loi_wizard_data';
const LOI_STEP_KEY = 'trados_loi_wizard_step';

const DEFAULT_FORM_DATA = {
  buyer: {
    logo_url: '',
    logo_size: 100,
    stamp_url: '',
    stamp_size: 100,
    stamp_rotation: 0,
    company_name: '',
    company_description: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    email: '',
    phone: '',
    website: ''
  },
  seller: {
    company_name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    email: '',
    phone: ''
  },
  product: {
    commodity_type: '',
    commodity_type_other: '',
    description: '',
    specifications: ''
  },
  terms: {
    quantity: '',
    quantity_unit: 'Metric Tons',
    price_per_unit: '',
    currency: 'USD',
    total_value: '',
    incoterms: '',
    payment_terms: '',
    origin_port: '',
    origin_port_other: '',
    destination_port: '',
    destination_port_other: '',
    request_timeframe: '',
    request_timeframe_other: '',
    bank_name: '',
    top_50_bank: '',
    proof_of_funds: '',
    proof_of_funds_details: ''
  },
  timeline: {
    validity_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    contract_duration: '',
    shipping_schedule: '',
    inspection_terms: '',
    special_conditions: ''
  },
  signatory: {
    full_name: '',
    title: '',
    email: '',
    signing_date: new Date().toISOString().split('T')[0],
    signature_image_url: '',
    signature_font: 'Great Vibes',
    signature_size: 100
  },
  extra_details: ''
};

function loadSavedFormData() {
  try {
    const saved = localStorage.getItem(LOI_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Deep merge with defaults to handle new fields
      const merged = { ...DEFAULT_FORM_DATA };
      // Preserve top-level non-object fields
      if (parsed.extra_details !== undefined) merged.extra_details = parsed.extra_details;
      for (const section of Object.keys(DEFAULT_FORM_DATA)) {
        if (typeof DEFAULT_FORM_DATA[section] !== 'object') continue;
        merged[section] = { ...DEFAULT_FORM_DATA[section], ...(parsed[section] || {}) };
      }
      return merged;
    }
  } catch (e) {
    console.warn('Failed to load saved LOI data:', e);
  }
  return DEFAULT_FORM_DATA;
}

function loadSavedStep() {
  try {
    const saved = localStorage.getItem(LOI_STEP_KEY);
    if (saved) {
      const step = parseInt(saved, 10);
      if (step >= 1 && step <= 5) return step;
    }
  } catch (e) {}
  return 1;
}

export default function LOICreationWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editLoiId = searchParams.get('edit');
  const isEmbed = searchParams.get('embed') === 'true';
  const codeInputsRef = useRef([]);

  // Embed mode is an iframe widget; editing an existing LOI is a private draft, not the canonical tool page
  useCanonical(!isEmbed && !editLoiId ? '/create-loi' : null);
  usePageTitle(!isEmbed && !editLoiId ? 'Create LOI' : null);

  const [currentStep, setCurrentStep] = useState(editLoiId ? 1 : loadSavedStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [loiId, setLoiId] = useState(editLoiId || null);
  const [isEditMode, setIsEditMode] = useState(!!editLoiId);
  const [editLoading, setEditLoading] = useState(!!editLoiId);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeSent, setCodeSent] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [formData, setFormData] = useState(editLoiId ? DEFAULT_FORM_DATA : loadSavedFormData);

  // --- Load existing LOI data when editing ---
  useEffect(() => {
    if (!editLoiId) return;
    const fetchLoiData = async () => {
      try {
        const res = await api.get(`/loi-creation/${editLoiId}`);
        if (res.data) {
          const loi = res.data;
          // Deep merge with defaults to ensure all fields exist
          const merged = { ...DEFAULT_FORM_DATA };
          for (const section of Object.keys(DEFAULT_FORM_DATA)) {
            if (typeof DEFAULT_FORM_DATA[section] !== 'object') {
              if (loi[section] !== undefined) merged[section] = loi[section];
              continue;
            }
            merged[section] = { ...DEFAULT_FORM_DATA[section], ...(loi[section] || {}) };
          }
          setFormData(merged);
          toast.info('LOI loaded for editing', { duration: 3000 });
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load LOI for editing');
        setIsEditMode(false);
      } finally {
        setEditLoading(false);
      }
    };
    fetchLoiData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLoiId]);

  // --- Show toast if resuming saved data ---
  useEffect(() => {
    if (editLoiId) return; // Skip resume toast when editing
    try {
      const saved = localStorage.getItem(LOI_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only show if they had meaningful data
        if (parsed.buyer?.company_name || parsed.seller?.company_name || parsed.product?.commodity_type) {
          toast.info('Resuming your saved LOI progress', { duration: 3000 });
        }
      }
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Pre-fill signatory from cookies for non-logged-in users ---
  useEffect(() => {
    if (!user) {
      setFormData(prev => {
        const updates = {};
        const cookieName = getCookie('trados_sig_name');
        const cookieTitle = getCookie('trados_sig_title');
        const cookieFont = getCookie('trados_sig_font');
        const cookieEmail = getCookie('trados_sig_email');
        if (!prev.signatory.full_name && cookieName) updates.full_name = cookieName;
        if (!prev.signatory.title && cookieTitle) updates.title = cookieTitle;
        if (!prev.signatory.email && cookieEmail) updates.email = cookieEmail;
        if (!prev.signatory.signature_font && cookieFont) updates.signature_font = cookieFont;
        if (Object.keys(updates).length === 0) return prev;
        return { ...prev, signatory: { ...prev.signatory, ...updates } };
      });
    }
  }, [user]);

  // --- Auto-fill for logged-in users (only fill EMPTY fields, skip in edit mode) ---
  useEffect(() => {
    if (user && !editLoiId) {
      const fetchCompanyProfile = async () => {
        try {
          const res = await api.get('/company');
          if (res.data) {
            setFormData(prev => {
              const b = prev.buyer;
              return {
                ...prev,
                buyer: {
                  ...b,
                  company_name: b.company_name || res.data.name || '',
                  address: b.address || res.data.address || '',
                  city: b.city || res.data.city || '',
                  state: b.state || res.data.state || '',
                  country: b.country || res.data.country || '',
                  zip: b.zip || res.data.zip || '',
                  email: b.email || res.data.email || user.email || '',
                  phone: b.phone || res.data.phone || '',
                  website: b.website || res.data.url || ''
                }
              };
            });
          }
        } catch (err) {
          // No company profile yet, that's fine
        }
      };
      fetchCompanyProfile();

      // Load saved font preference and auto-fill signatory info
      api.get('/users/me/preferences').then(res => {
        if (res.data?.signature_font) {
          setFormData(prev => ({
            ...prev,
            signatory: { ...prev.signatory, signature_font: res.data.signature_font }
          }));
        }
      }).catch(() => {});

      // Auto-fill signatory name, title, email from user profile
      api.get('/users/me').then(res => {
        if (res.data) {
          setFormData(prev => {
            const updates = {};
            if (!prev.signatory.full_name) {
              const name = [res.data.first_name, res.data.last_name].filter(Boolean).join(' ');
              if (name) updates.full_name = name;
            }
            if (!prev.signatory.email && res.data.email) {
              updates.email = res.data.email;
            }
            if (Object.keys(updates).length === 0) return prev;
            return { ...prev, signatory: { ...prev.signatory, ...updates } };
          });
        }
      }).catch(() => {});
    }
  }, [user]);

  // --- Persist form data to localStorage ---
  useEffect(() => {
    // Don't save data URLs (too large) — only save uploaded URLs
    const dataToSave = { ...formData };
    if (dataToSave.signatory?.signature_image_url?.startsWith('data:')) {
      dataToSave.signatory = { ...dataToSave.signatory, signature_image_url: '' };
    }
    try {
      localStorage.setItem(LOI_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('Failed to save LOI data:', e);
    }
    // Save signatory info to cookies for returning visitors
    if (formData.signatory.full_name) setCookie('trados_sig_name', formData.signatory.full_name);
    if (formData.signatory.title) setCookie('trados_sig_title', formData.signatory.title);
    if (formData.signatory.email) setCookie('trados_sig_email', formData.signatory.email);
    if (formData.signatory.signature_font) setCookie('trados_sig_font', formData.signatory.signature_font);
  }, [formData]);

  // --- Persist step to localStorage ---
  useEffect(() => {
    try {
      localStorage.setItem(LOI_STEP_KEY, String(currentStep));
    } catch (e) {}
  }, [currentStep]);

  // --- Auto-calculate total value ---
  useEffect(() => {
    const qty = parseFloat(formData.terms.quantity) || 0;
    const price = parseFloat(formData.terms.price_per_unit) || 0;
    const total = qty * price;
    if (total > 0) {
      setFormData(prev => ({
        ...prev,
        terms: { ...prev.terms, total_value: total.toFixed(2) }
      }));
    }
  }, [formData.terms.quantity, formData.terms.price_per_unit]);

  // --- Helpers ---
  const updateField = useCallback((section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  }, []);

  const handleFileUpload = async (e, field, label, uploadType = 'logo') => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = uploadType === 'signature' ? 2 : 5;
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`${label} must be under ${maxSize}MB`);
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const prefix = user ? '/loi-creation' : '/public/loi-creation';
      const endpoint = `${prefix}/upload-${uploadType}`;
      const res = await api.post(endpoint, fd);
      const section = uploadType === 'signature' ? 'signatory' : 'buyer';
      const targetField = uploadType === 'signature' ? 'signature_image_url' : field;
      updateField(section, targetField, res.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(`Failed to upload ${label.toLowerCase()}`);
    }
  };

  const handleLogoUpload = (e) => handleFileUpload(e, 'logo_url', 'Logo', 'logo');
  const handleStampUpload = (e) => handleFileUpload(e, 'stamp_url', 'Company stamp', 'stamp');

  // Upload signature data URL to DO Spaces
  const uploadSignatureDataUrl = async (dataUrl) => {
    if (!dataUrl || !dataUrl.startsWith('data:')) return dataUrl;
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'signature.png', { type: 'image/png' });
      const fd = new FormData();
      fd.append('file', file);
      const prefix = user ? '/loi-creation' : '/public/loi-creation';
      const uploadRes = await api.post(`${prefix}/upload-signature`, fd);
      return uploadRes.data.url;
    } catch (err) {
      console.error('Failed to upload signature:', err);
      return dataUrl; // Fallback to data URL
    }
  };

  // --- Step Validation ---
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.buyer.company_name) { toast.error('Company name is required'); return false; }
        if (!formData.buyer.address) { toast.error('Address is required'); return false; }
        if (!formData.buyer.city) { toast.error('City is required'); return false; }
        if (!formData.buyer.country) { toast.error('Country is required'); return false; }
        if (!formData.buyer.email) { toast.error('Email is required'); return false; }
        if (!formData.buyer.phone) { toast.error('Phone is required'); return false; }
        return true;
      case 2:
        if (!formData.seller.company_name) { toast.error('Seller company name is required'); return false; }
        if (!formData.seller.address) { toast.error('Seller address is required'); return false; }
        if (!formData.seller.email) { toast.error('Seller email is required'); return false; }
        if (!formData.product.commodity_type) { toast.error('Commodity type is required'); return false; }
        if (formData.product.commodity_type === 'Other' && !formData.product.commodity_type_other) { toast.error('Please specify the commodity type'); return false; }
        if (!formData.product.description) { toast.error('Product description is required'); return false; }
        return true;
      case 3:
        if (!formData.terms.quantity) { toast.error('Quantity is required'); return false; }
        if (!formData.terms.price_per_unit) { toast.error('Price per unit is required'); return false; }
        if (!formData.terms.incoterms) { toast.error('Incoterms is required'); return false; }
        if (!formData.terms.payment_terms) { toast.error('Payment terms is required'); return false; }
        if (!formData.terms.origin_port) { toast.error('Port of origin is required'); return false; }
        if (formData.terms.origin_port === 'Other' && !formData.terms.origin_port_other) { toast.error('Please specify the port of origin'); return false; }
        if (!formData.terms.request_timeframe) { toast.error('Request timeframe is required'); return false; }
        if (formData.terms.request_timeframe === 'Other' && !formData.terms.request_timeframe_other) { toast.error('Please specify the timeframe'); return false; }
        if (!formData.terms.bank_name) { toast.error('Bank name is required'); return false; }
        if (!formData.terms.top_50_bank) { toast.error('Please indicate if Top 50 bank'); return false; }
        if (!formData.terms.proof_of_funds) { toast.error('Proof of funds availability is required'); return false; }
        if (formData.terms.proof_of_funds === 'Yes' && !formData.terms.proof_of_funds_details) { toast.error('Please provide proof of funds details'); return false; }
        return true;
      case 4:
        if (!formData.timeline.validity_date) { toast.error('Validity date is required'); return false; }
        if (!formData.timeline.contract_duration) { toast.error('Contract duration is required'); return false; }
        if (!formData.timeline.inspection_terms) { toast.error('Inspection terms is required'); return false; }
        return true;
      default:
        return true;
    }
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Submit & Download ---
  const handleSubmit = async () => {
    if (!formData.signatory.full_name) { toast.error('Signatory name is required'); return; }
    if (!formData.signatory.title) { toast.error('Signatory title is required'); return; }
    if (!formData.signatory.signature_image_url) {
      setSignatureError(true);
      toast.error('Please provide a signature before submitting');
      return;
    }

    setIsSubmitting(true);
    try {
      let newLoiId;
      if (isEditMode && loiId) {
        // Update existing LOI
        await api.put(`/loi-creation/${loiId}`, formData);
        newLoiId = loiId;
        toast.success('LOI updated successfully!');
      } else {
        // Create new LOI
        const endpoint = user ? '/loi-creation/create' : '/public/loi-creation/create';
        const res = await api.post(endpoint, formData);
        newLoiId = res.data.id;
        setLoiId(newLoiId);
        toast.success('LOI created successfully!');
      }

      // Clear saved data on success
      localStorage.removeItem(LOI_STORAGE_KEY);
      localStorage.removeItem(LOI_STEP_KEY);

      // Auto-download after save for logged-in users
      if (user && newLoiId) {
        handleDownload(newLoiId);
      }

      if (!user) {
        // Pre-fill verification email from signatory or buyer email
        if (!verificationEmail) {
          setVerificationEmail(formData.signatory.email || formData.buyer.email || '');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEditMode ? 'Failed to update LOI' : 'Failed to create LOI'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const loiIdToUse = id || loiId;
      // Update the LOI on the server with current form data before downloading
      if (user && loiIdToUse) {
        try {
          await api.put(`/loi-creation/${loiIdToUse}`, formData);
        } catch (updateErr) {
          console.warn('Failed to update LOI before download:', updateErr);
        }
      }
      const endpoint = user ? `/loi-creation/${loiIdToUse}/download` : `/public/loi-creation/${loiIdToUse}/download`;
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LOI-${formData.buyer.company_name || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('LOI downloaded!');
    } catch (err) {
      toast.error('Failed to download LOI');
    }
  };

  // --- Email Verification (public users) ---
  const handleSendVerification = async () => {
    if (!verificationEmail) { toast.error('Please enter your email'); return; }
    if (!loiId) { toast.error('Please create the LOI first'); return; }

    try {
      await api.post(`/public/loi-creation/${loiId}/send-verification`, { email: verificationEmail });
      setCodeSent(true);
      toast.success('Verification code sent to your email!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification');
    }
  };

  const handleChangeEmail = () => {
    setCodeSent(false);
    setVerificationCode(['', '', '', '', '', '']);
    setEmailVerified(false);
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) { toast.error('Please enter the full 6-digit code'); return; }

    try {
      await api.post(`/public/loi-creation/${loiId}/verify-email`, { code });
      setEmailVerified(true);
      toast.success('Email verified! Your LOI has been sent to your email.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste of full code
      const pasted = value.replace(/\D/g, '').slice(0, 6);
      if (pasted.length > 1) {
        const newCode = [...verificationCode];
        for (let i = 0; i < 6; i++) {
          newCode[i] = pasted[i] || '';
        }
        setVerificationCode(newCode);
        const focusIdx = Math.min(pasted.length, 5);
        codeInputsRef.current[focusIdx]?.focus();
        return;
      }
    }

    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);

    if (digit && index < 5) {
      codeInputsRef.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputsRef.current[index - 1]?.focus();
    }
  };

  // --- Render Steps ---

  const renderStep1 = () => (
    <div className="space-y-5">
      <SectionTitle>Your Company (Buyer)</SectionTitle>

      {/* Logo Upload */}
      <div className="space-y-1.5">
        <Label className="text-sm text-gray-700">Company Logo</Label>
        <div
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
          onClick={() => document.getElementById('logo-upload').click()}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-400', 'bg-blue-50'); }}
          onDragLeave={(e) => { e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50'); }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
            const file = e.dataTransfer.files[0];
            if (file) {
              const input = document.getElementById('logo-upload');
              const dt = new DataTransfer();
              dt.items.add(file);
              input.files = dt.files;
              handleLogoUpload({ target: { files: [file] } });
            }
          }}
        >
          {formData.buyer.logo_url ? (
            <div className="flex flex-col items-center gap-2">
              <img src={formData.buyer.logo_url} alt="Logo" className="h-16 object-contain" />
              <span className="text-xs text-gray-500">Click or drag to replace</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-sm text-gray-500">Drag & drop your logo or click to browse</span>
              <span className="text-xs text-gray-400">PNG, JPG up to 2MB</span>
            </div>
          )}
          <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
        </div>
        {formData.buyer.logo_url && (
          <div className="flex items-center gap-4 mt-2">
            <Label className="text-xs text-gray-500 w-16">Logo Size</Label>
            <input
              type="range"
              min={50}
              max={200}
              value={formData.buyer.logo_size || 100}
              onChange={(e) => updateField('buyer', 'logo_size', parseInt(e.target.value))}
              className="flex-1 accent-[#2B4B6A]"
            />
            <span className="text-xs text-gray-500 w-10 text-center">{formData.buyer.logo_size || 100}%</span>
          </div>
        )}
      </div>

      <FieldGroup label="Company Name" required>
        <Input
          value={formData.buyer.company_name}
          onChange={(e) => updateField('buyer', 'company_name', e.target.value)}
          placeholder="e.g. Global Trading Corp"
        />
      </FieldGroup>

      <FieldGroup label="Address" required>
        <Input
          value={formData.buyer.address}
          onChange={(e) => updateField('buyer', 'address', e.target.value)}
          placeholder="Street address"
        />
      </FieldGroup>

      <FieldRow>
        <FieldGroup label="City" required>
          <Input
            value={formData.buyer.city}
            onChange={(e) => updateField('buyer', 'city', e.target.value)}
            placeholder="City"
          />
        </FieldGroup>
        <FieldGroup label="State / Province">
          <Input
            value={formData.buyer.state}
            onChange={(e) => updateField('buyer', 'state', e.target.value)}
            placeholder="State"
          />
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Country" required>
          <Select value={formData.buyer.country} onValueChange={(v) => updateField('buyer', 'country', v)}>
            <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="ZIP / Postal Code">
          <Input
            value={formData.buyer.zip}
            onChange={(e) => updateField('buyer', 'zip', e.target.value)}
            placeholder="ZIP code"
          />
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Email" required>
          <Input
            type="email"
            value={formData.buyer.email}
            onChange={(e) => updateField('buyer', 'email', e.target.value)}
            placeholder="company@example.com"
          />
        </FieldGroup>
        <FieldGroup label="Phone" required>
          <Input
            type="tel"
            value={formData.buyer.phone}
            onChange={(e) => updateField('buyer', 'phone', e.target.value)}
            placeholder="+1 (555) 000-0000"
          />
        </FieldGroup>
      </FieldRow>

      <FieldGroup label="Website">
        <Input
          value={formData.buyer.website}
          onChange={(e) => updateField('buyer', 'website', e.target.value)}
          placeholder="https://www.example.com"
        />
      </FieldGroup>

      <FieldGroup label="Company Description">
        <Textarea
          value={formData.buyer.company_description}
          onChange={(e) => updateField('buyer', 'company_description', e.target.value)}
          placeholder="Brief description of your company, industry, and expertise..."
          rows={3}
        />
      </FieldGroup>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <SectionTitle>Seller Information</SectionTitle>

      <FieldGroup label="Seller Company Name" required>
        <Input
          value={formData.seller.company_name}
          onChange={(e) => updateField('seller', 'company_name', e.target.value)}
          placeholder="e.g. Brazil Exports Ltda"
        />
      </FieldGroup>

      <FieldGroup label="Seller Address" required>
        <Input
          value={formData.seller.address}
          onChange={(e) => updateField('seller', 'address', e.target.value)}
          placeholder="Street address"
        />
      </FieldGroup>

      <FieldRow>
        <FieldGroup label="Seller Email" required>
          <Input
            type="email"
            value={formData.seller.email}
            onChange={(e) => updateField('seller', 'email', e.target.value)}
            placeholder="seller@example.com"
          />
        </FieldGroup>
        <FieldGroup label="Seller Phone">
          <Input
            type="tel"
            value={formData.seller.phone}
            onChange={(e) => updateField('seller', 'phone', e.target.value)}
            placeholder="+55 (11) 0000-0000"
          />
        </FieldGroup>
      </FieldRow>

      <div className="border-t border-gray-200 my-6" />

      <SectionTitle>Product Details</SectionTitle>

      <FieldRow>
        <FieldGroup label="Commodity Type" required>
          <Select value={formData.product.commodity_type} onValueChange={(v) => updateField('product', 'commodity_type', v)}>
            <SelectTrigger><SelectValue placeholder="Select commodity" /></SelectTrigger>
            <SelectContent>
              {COMMODITY_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
        {formData.product.commodity_type === 'Other' && (
          <FieldGroup label="Specify Commodity" required>
            <Input
              value={formData.product.commodity_type_other}
              onChange={(e) => updateField('product', 'commodity_type_other', e.target.value)}
              placeholder="e.g. Palm Oil, Cotton, etc."
            />
          </FieldGroup>
        )}
      </FieldRow>

      <FieldGroup label="Product Description" required>
        <Textarea
          value={formData.product.description}
          onChange={(e) => updateField('product', 'description', e.target.value)}
          placeholder="Describe the product, grade, and any relevant details..."
          rows={2}
        />
      </FieldGroup>

      <FieldGroup label="Quality Specifications">
        <Textarea
          value={formData.product.specifications}
          onChange={(e) => updateField('product', 'specifications', e.target.value)}
          placeholder="Moisture content, purity, certifications, etc. (optional)"
          rows={2}
        />
      </FieldGroup>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <SectionTitle>Deal Terms</SectionTitle>

      <FieldRow cols={3}>
        <FieldGroup label="Quantity" required>
          <Input
            type="number"
            value={formData.terms.quantity}
            onChange={(e) => updateField('terms', 'quantity', e.target.value)}
            placeholder="e.g. 5000"
          />
        </FieldGroup>
        <FieldGroup label="Unit">
          <Select value={formData.terms.quantity_unit} onValueChange={(v) => updateField('terms', 'quantity_unit', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {QUANTITY_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Currency">
          <Select value={formData.terms.currency} onValueChange={(v) => updateField('terms', 'currency', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Target Price per Unit" required>
          <Input
            type="number"
            value={formData.terms.price_per_unit}
            onChange={(e) => updateField('terms', 'price_per_unit', e.target.value)}
            placeholder="e.g. 450.00"
          />
        </FieldGroup>
        <FieldGroup label="Total Value (auto-calculated)">
          <Input
            value={formData.terms.total_value ? `${formData.terms.currency} ${Number(formData.terms.total_value).toLocaleString()}` : ''}
            readOnly
            className="bg-gray-50"
            placeholder="Calculated automatically"
          />
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Incoterms" required>
          <Select value={formData.terms.incoterms} onValueChange={(v) => updateField('terms', 'incoterms', v)}>
            <SelectTrigger><SelectValue placeholder="Select Incoterms" /></SelectTrigger>
            <SelectContent>
              {INCOTERMS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Payment Terms" required>
          <Select value={formData.terms.payment_terms} onValueChange={(v) => updateField('terms', 'payment_terms', v)}>
            <SelectTrigger><SelectValue placeholder="Select payment terms" /></SelectTrigger>
            <SelectContent>
              {PAYMENT_TERMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Port of Origin" required>
          <SearchablePortSelect
            value={formData.terms.origin_port}
            onChange={(v) => updateField('terms', 'origin_port', v)}
            placeholder="Search ports..."
          />
        </FieldGroup>
        <FieldGroup label="Port of Destination">
          <SearchablePortSelect
            value={formData.terms.destination_port}
            onChange={(v) => updateField('terms', 'destination_port', v)}
            placeholder="Search ports... (optional)"
          />
        </FieldGroup>
      </FieldRow>

      {formData.terms.origin_port === 'Other' && (
        <FieldGroup label="Specify Origin Port" required>
          <Input
            value={formData.terms.origin_port_other}
            onChange={(e) => updateField('terms', 'origin_port_other', e.target.value)}
            placeholder="e.g. Port Name - Country"
          />
        </FieldGroup>
      )}
      {formData.terms.destination_port === 'Other' && (
        <FieldGroup label="Specify Destination Port">
          <Input
            value={formData.terms.destination_port_other}
            onChange={(e) => updateField('terms', 'destination_port_other', e.target.value)}
            placeholder="e.g. Port Name - Country"
          />
        </FieldGroup>
      )}

      <div className="border-t border-gray-200 my-6" />
      <SectionTitle>Financial Details</SectionTitle>

      <FieldRow>
        <FieldGroup label="Request Timeframe" required>
          <Select value={formData.terms.request_timeframe} onValueChange={(v) => updateField('terms', 'request_timeframe', v)}>
            <SelectTrigger><SelectValue placeholder="Select timeframe" /></SelectTrigger>
            <SelectContent>
              {REQUEST_TIMEFRAMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
        {formData.terms.request_timeframe === 'Other' && (
          <FieldGroup label="Specify Timeframe" required>
            <Input
              value={formData.terms.request_timeframe_other}
              onChange={(e) => updateField('terms', 'request_timeframe_other', e.target.value)}
              placeholder="e.g. 18 months"
            />
          </FieldGroup>
        )}
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Bank Name" required>
          <Input
            value={formData.terms.bank_name}
            onChange={(e) => updateField('terms', 'bank_name', e.target.value)}
            placeholder="e.g. JPMorgan Chase, HSBC"
          />
        </FieldGroup>
        <FieldGroup label="Top 50 Bank?" required>
          <Select value={formData.terms.top_50_bank} onValueChange={(v) => updateField('terms', 'top_50_bank', v)}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Proof of Funds Available?" required>
          <Select value={formData.terms.proof_of_funds} onValueChange={(v) => updateField('terms', 'proof_of_funds', v)}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        {formData.terms.proof_of_funds === 'Yes' && (
          <FieldGroup label="How? (Details)" required>
            <Input
              value={formData.terms.proof_of_funds_details}
              onChange={(e) => updateField('terms', 'proof_of_funds_details', e.target.value)}
              placeholder="e.g. Bank statement, MT199, BCL"
            />
          </FieldGroup>
        )}
      </FieldRow>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      <SectionTitle>Timeline & Conditions</SectionTitle>

      <FieldRow>
        <FieldGroup label="LOI Validity Date" required>
          <Input
            type="date"
            value={formData.timeline.validity_date}
            onChange={(e) => updateField('timeline', 'validity_date', e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Contract Duration" required>
          <Select value={formData.timeline.contract_duration} onValueChange={(v) => updateField('timeline', 'contract_duration', v)}>
            <SelectTrigger><SelectValue placeholder="Select duration" /></SelectTrigger>
            <SelectContent>
              {CONTRACT_DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldGroup>
      </FieldRow>

      <FieldGroup label="Shipping Schedule">
        <Textarea
          value={formData.timeline.shipping_schedule}
          onChange={(e) => updateField('timeline', 'shipping_schedule', e.target.value)}
          placeholder="e.g. Monthly shipments of 1,000 MT starting June 2026 (optional)"
          rows={2}
        />
      </FieldGroup>

      <FieldGroup label="Inspection Terms" required>
        <Select value={formData.timeline.inspection_terms} onValueChange={(v) => updateField('timeline', 'inspection_terms', v)}>
          <SelectTrigger><SelectValue placeholder="Select inspection terms" /></SelectTrigger>
          <SelectContent>
            {INSPECTION_TERMS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
      </FieldGroup>

      <FieldGroup label="Special Conditions">
        <Textarea
          value={formData.timeline.special_conditions}
          onChange={(e) => updateField('timeline', 'special_conditions', e.target.value)}
          placeholder="Any additional terms, conditions, or notes (optional)"
          rows={3}
        />
      </FieldGroup>

      <div className="border-t border-gray-200 my-6" />

      <FieldGroup label="Extra LOI Details and Requirements">
        <div className="relative">
          <Textarea
            value={formData.extra_details}
            onChange={(e) => {
              if (e.target.value.length <= 1000) {
                setFormData(prev => ({ ...prev, extra_details: e.target.value }));
              }
            }}
            placeholder="Any additional details, requirements, or instructions to include in the LOI document (optional)"
            rows={5}
            className="resize-y"
          />
          <span className="absolute bottom-2 right-3 text-xs text-gray-400">
            {formData.extra_details.length}/1000
          </span>
        </div>
      </FieldGroup>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-5">
      <SectionTitle>Review & Sign</SectionTitle>

      <FieldRow>
        <FieldGroup label="Signatory Full Name" required>
          <Input
            value={formData.signatory.full_name}
            onChange={(e) => updateField('signatory', 'full_name', e.target.value)}
            placeholder="e.g. John Smith"
          />
        </FieldGroup>
        <FieldGroup label="Title / Position" required>
          <Input
            value={formData.signatory.title}
            onChange={(e) => updateField('signatory', 'title', e.target.value)}
            placeholder="e.g. CEO, Director of Procurement"
          />
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        <FieldGroup label="Email Address">
          <Input
            type="email"
            value={formData.signatory.email}
            onChange={(e) => updateField('signatory', 'email', e.target.value)}
            placeholder="signatory@company.com"
          />
        </FieldGroup>
        <FieldGroup label="Signing Date">
          <Input
            type="date"
            value={formData.signatory.signing_date}
            onChange={(e) => updateField('signatory', 'signing_date', e.target.value)}
          />
        </FieldGroup>
      </FieldRow>

      <FieldGroup label="Signature" required>
        <LOISignaturePad
          initialFont={formData.signatory.signature_font}
          initialName={formData.signatory.full_name}
          signatureSize={formData.signatory.signature_size || 100}
          hasError={signatureError}
          onSignatureChange={async (dataUrl) => {
            setSignatureError(false);
            if (dataUrl) {
              updateField('signatory', 'signature_image_url', dataUrl);
              const uploadedUrl = await uploadSignatureDataUrl(dataUrl);
              if (uploadedUrl !== dataUrl) {
                updateField('signatory', 'signature_image_url', uploadedUrl);
              }
            } else {
              updateField('signatory', 'signature_image_url', '');
            }
          }}
          onFontChange={(fontName) => {
            updateField('signatory', 'signature_font', fontName);
            // Save preference for logged-in users
            if (user) {
              api.put('/users/me/preferences', { signature_font: fontName }).catch(() => {});
            }
          }}
        />
        <div className="flex items-center gap-4 mt-2">
          <Label className="text-xs text-gray-500 w-24">Signature Size</Label>
          <input
            type="range"
            min={50}
            max={200}
            value={formData.signatory.signature_size || 100}
            onChange={(e) => updateField('signatory', 'signature_size', parseInt(e.target.value))}
            className="flex-1 accent-[#2B4B6A]"
          />
          <span className="text-xs text-gray-500 w-10 text-center">{formData.signatory.signature_size || 100}%</span>
        </div>
      </FieldGroup>

      {/* Company Stamp Upload */}
      <div className="space-y-1.5">
        <Label className="text-sm text-gray-700">Company Stamp / Seal</Label>
        <div
          className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors cursor-pointer"
          onClick={() => document.getElementById('stamp-upload').click()}
        >
          {formData.buyer.stamp_url ? (
            <div className="flex flex-col items-center gap-2">
              <img src={formData.buyer.stamp_url} alt="Stamp" className="h-14 object-contain" />
              <span className="text-xs text-gray-500">Click to replace stamp</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm text-gray-500">Upload company stamp or seal</span>
              <span className="text-xs text-gray-400">PNG, JPG up to 2MB (optional)</span>
            </div>
          )}
          <input id="stamp-upload" type="file" accept="image/*" className="hidden" onChange={handleStampUpload} />
        </div>
        {formData.buyer.stamp_url && (
          <>
            <div className="flex items-center gap-4 mt-2">
              <Label className="text-xs text-gray-500 w-16">Size</Label>
              <input
                type="range"
                min={50}
                max={200}
                value={formData.buyer.stamp_size || 100}
                onChange={(e) => updateField('buyer', 'stamp_size', parseInt(e.target.value))}
                className="flex-1 accent-[#2B4B6A]"
              />
              <span className="text-xs text-gray-500 w-10 text-center">{formData.buyer.stamp_size || 100}%</span>
            </div>
            <div className="flex items-center gap-4 mt-1">
              <Label className="text-xs text-gray-500 w-16">Rotation</Label>
              <input
                type="range"
                min={-25}
                max={25}
                value={formData.buyer.stamp_rotation || 0}
                onChange={(e) => updateField('buyer', 'stamp_rotation', parseInt(e.target.value))}
                className="flex-1 accent-[#2B4B6A]"
              />
              <span className="text-xs text-gray-500 w-10 text-center">{formData.buyer.stamp_rotation || 0}&deg;</span>
            </div>
          </>
        )}
      </div>

      {/* Create/Update LOI button */}
      {(!loiId || isEditMode) && (
        <div className="pt-4">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#2B4B6A] to-[#1e3a52] hover:from-[#3a6590] hover:to-[#2B4B6A] text-white rounded-xl shadow-lg"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {isEditMode ? 'Saving Changes...' : 'Creating LOI...'}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {isEditMode ? 'Save Changes & Download LOI' : user ? 'Create & Download LOI' : 'Create LOI'}
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Logged-in user: download again */}
      {user && loiId && (
        <div className="pt-4">
          <Button
            onClick={() => handleDownload()}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] hover:from-[#4ade80] hover:to-[#22c55e] text-white rounded-xl shadow-lg"
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download LOI PDF
            </span>
          </Button>
        </div>
      )}

      {/* Public user: email verification required after LOI is created */}
      {!user && loiId && (
        <Card className="border-blue-200 bg-blue-50/50 mt-6">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-blue-700 mb-1">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold text-sm">Email Verification Required</span>
            </div>
            <p className="text-sm text-gray-600">
              Your LOI has been created. To receive your LOI document, please verify your email address. We'll send a 6-digit code.
            </p>

            {!codeSent ? (
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={verificationEmail}
                  onChange={(e) => setVerificationEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1"
                />
                <Button onClick={handleSendVerification} className="bg-[#2B4B6A] hover:bg-[#3a6590] text-white whitespace-nowrap">
                  Send Code
                </Button>
              </div>
            ) : !emailVerified ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Code sent to <span className="font-medium">{verificationEmail}</span>
                  </p>
                  <button
                    onClick={handleChangeEmail}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Change email
                  </button>
                </div>
                <div className="flex gap-2 justify-center">
                  {verificationCode.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => { codeInputsRef.current[idx] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleCodeChange(idx, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                      className="w-11 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleVerifyCode} className="flex-1 bg-[#2B4B6A] hover:bg-[#3a6590] text-white">
                    Verify
                  </Button>
                  <Button variant="ghost" onClick={() => { setCodeSent(false); setVerificationCode(['', '', '', '', '', '']); }} className="text-sm">
                    Resend
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium text-sm">Email verified! Your LOI has been sent to {verificationEmail}</span>
                </div>
                <Button
                  onClick={() => handleDownload()}
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#2B4B6A] to-[#1e3a52] hover:from-[#3a6590] hover:to-[#2B4B6A] text-white rounded-xl shadow-lg"
                >
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download LOI PDF
                  </span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  // --- Main Render ---
  if (editLoading) {
    return user ? (
      <Sidebar>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#2B4B6A] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading LOI data...</p>
          </div>
        </div>
      </Sidebar>
    ) : (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2B4B6A] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading LOI data...</p>
        </div>
      </div>
    );
  }

  const wizardContent = (
    <div className={user && !isFullscreen ? "" : "min-h-screen bg-gray-50"}>
      {/* Top Bar - Public: shared yellow header; Logged in: dark header with step info */}
      {!user && !isEmbed ? (
        <WPChrome type="header" fallback={<TradosPublicHeader />} />
      ) : (
        <div className="bg-[#1e2a3a] text-white px-6 py-3 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            {!isFullscreen && (
              <button
                onClick={() => navigate(-1)}
                className="text-sm text-white/80 hover:text-white flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            )}
            {isFullscreen && (
              <span className="text-xl font-bold tracking-tight">Trados</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium opacity-90">
              {isEditMode ? 'Edit' : 'Create'} Your Letter of Intent &mdash; Step {currentStep} of 5
            </span>
            {isFullscreen && (
              <button
                onClick={() => setIsFullscreen(false)}
                className="text-sm text-white/80 hover:text-white transition-colors flex items-center gap-1.5"
                title="Close full screen"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Close Full Screen
              </button>
            )}
            <button
              onClick={() => {
                if (window.confirm('Start a fresh LOI? All current data will be cleared.')) {
                  localStorage.removeItem(LOI_STORAGE_KEY);
                  localStorage.removeItem(LOI_STEP_KEY);
                  setFormData({ ...DEFAULT_FORM_DATA, signatory: { ...DEFAULT_FORM_DATA.signatory, signing_date: new Date().toISOString().split('T')[0] } });
                  setCurrentStep(1);
                  setLoiId(null);
                  setEmailVerified(false);
                  setCodeSent(false);
                  toast.success('Form cleared');
                }
              }}
              className="text-xs text-white/60 hover:text-white/90 transition-colors underline underline-offset-2"
            >
              Start Fresh
            </button>
          </div>
        </div>
      )}

      {/* Step Indicator */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto">
          <StepIndicator currentStep={currentStep} />
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-col lg:flex-row gap-0 min-h-[calc(100vh-120px)]">
        {/* Left: Form Panel */}
        <div className="w-full lg:w-[45%] p-4 lg:p-6">
          <div className="bg-white rounded-2xl p-6 lg:p-8 border border-gray-200 shadow-sm">
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={goBack} className="gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 5 && (
                <Button onClick={goNext} className="bg-[#2B4B6A] hover:bg-[#3a6590] text-white gap-1.5">
                  Continue
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Right: Preview Panel (desktop) */}
        <div className="hidden lg:block flex-1 p-6 sticky top-[120px] max-h-[calc(100vh-120px)] overflow-y-auto">
          <LOIPreview formData={formData} isFullScreen={isFullscreen} onToggleFullScreen={() => setIsFullscreen(!isFullscreen)} />
        </div>
      </div>

      {/* Mobile Preview Toggle */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <Button
          onClick={() => setShowMobilePreview(!showMobilePreview)}
          className="rounded-full h-12 w-12 p-0 bg-[#2B4B6A] hover:bg-[#3a6590] text-white shadow-xl"
        >
          {showMobilePreview ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </Button>
      </div>

      {/* Mobile Preview Panel */}
      {showMobilePreview && (
        <div className="lg:hidden fixed inset-0 z-30 bg-gray-50 overflow-y-auto pt-16">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">LOI Preview</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowMobilePreview(false)}>
                Close
              </Button>
            </div>
            <LOIPreview formData={formData} isFullScreen={false} onToggleFullScreen={() => { setShowMobilePreview(false); setIsFullscreen(true); }} />
          </div>
        </div>
      )}

      {/* Build Number - only on public (Sidebar already has its own) */}
      {!user && !isEmbed && (
        <div className="text-center py-2">
          <span className="text-xs text-gray-400 font-mono">Build: {BUILD_ID}</span>
        </div>
      )}

      {!user && !isEmbed && <WPChrome type="footer" />}
    </div>
  );

  // Logged-in users: wrap in Sidebar unless fullscreen
  if (user && !isFullscreen) {
    return (
      <Sidebar>
        {wizardContent}
      </Sidebar>
    );
  }

  // Public users or fullscreen mode: render standalone
  return wizardContent;
}
